import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  MapPin,
  Clock,
  Trash2,
  X,
  Send,
  Mail,
  Building2,
  User,
  Users,
  Pencil,
  Eye,
} from 'lucide-react';
import { usePlanner, subscribeCalendarChanged } from '../context/PlannerContext';
import SelectField from './SelectField';
import DateTimePicker from './DateTimePicker';
import {
  WEEKDAYS,
  CAMPAIGN_START_COLOR,
  CAMPAIGN_FOLLOWUP_COLOR,
  CAMPAIGN_START_LABEL,
  CAMPAIGN_FOLLOWUP_LABEL,
  USER_EVENT_COLOR,
  USER_EVENT_LABEL,
  toDateKey,
  parseDate,
  sameDay,
  formatMonthTitle,
  formatThreeWeekTitle,
  formatEventTime,
  buildMonthCells,
  buildThreeWeekCells,
  getCalendarFetchRange,
  groupEventsByDay,
  findBusyConflicts,
  resolveCalendarEventColor,
  eventColorRgba,
  filterCalendarEventMembers,
} from '../utils/calendarEvents';

const EVENT_COLORS = [
  USER_EVENT_COLOR, '#6d28d9', '#8b5cf6', '#a78bfa',
  '#0369a1', '#0284c7', '#0ea5e9',
  '#ea580c', '#c2410c', '#d97706',
  '#be123c', '#db2777',
  '#4f46e5', '#6366f1',
  '#475569', '#334155',
];

const formatTime = formatEventTime;

const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Mo' },
  { value: 2, label: 'Di' },
  { value: 3, label: 'Mi' },
  { value: 4, label: 'Do' },
  { value: 5, label: 'Fr' },
  { value: 6, label: 'Sa' },
  { value: 7, label: 'So' },
];

const DURATION_PRESETS = [
  { value: 15, label: '15 Min.' },
  { value: 30, label: '30 Min.' },
  { value: 45, label: '45 Min.' },
  { value: 60, label: '1 Std.' },
  { value: 90, label: '1,5 Std.' },
  { value: 120, label: '2 Std.' },
  { value: 180, label: '3 Std.' },
  { value: 240, label: '4 Std.' },
];

const ALL_DAY_MINUTES = 1440;

const toLocalInput = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const isoWeekdayFromDate = (d) => {
  const js = d.getDay();
  return js === 0 ? 7 : js;
};

const resolveSeriesId = (ev) => {
  if (ev?.seriesId != null) return ev.seriesId;
  const raw = String(ev?.id ?? '');
  if (raw.includes('::')) return Number(raw.split('::')[0]);
  return ev?.id;
};

const applyDurationToEnd = (startAt, durationMinutes) => {
  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) return '';
  return toLocalInput(new Date(start.getTime() + Math.max(15, Number(durationMinutes) || 60) * 60 * 1000));
};

/** Ganztägig: lokaler Mitternacht-Start, Ende = nächster Tag 00:00 (exklusiv). */
const toAllDayBounds = (startAt) => {
  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) return { startAt: '', endAt: '' };
  const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0);
  const dayEndExclusive = new Date(dayStart);
  dayEndExclusive.setDate(dayEndExclusive.getDate() + 1);
  return {
    startAt: toLocalInput(dayStart),
    endAt: toLocalInput(dayEndExclusive),
  };
};

const recurrenceLabel = (ev) => {
  const r = String(ev?.recurrence || 'none');
  if (r === 'daily') return 'Täglich';
  if (r === 'weekly') return 'Wöchentlich';
  if (r === 'monthly') return 'Monatlich (Datum)';
  return null;
};

const formatDateLong = (dateString) => {
  if (!dateString) return '—';
  const d = parseDate(dateString) || new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const campaignStatusLabel = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'sent') return 'Aktiv';
  if (s === 'paused') return 'Pausiert';
  if (s === 'completed') return 'Abgeschlossen';
  if (s === 'cancelled') return 'Abgebrochen';
  if (s === 'draft') return 'Entwurf';
  return status || '—';
};

const emptyForm = (day) => {
  const base = day ? new Date(day) : new Date();
  base.setMinutes(0, 0, 0);
  const durationMinutes = 60;
  return {
    title: '',
    description: '',
    location: '',
    startAt: toLocalInput(base),
    endAt: applyDurationToEnd(toLocalInput(base), durationMinutes),
    allDay: false,
    color: EVENT_COLORS[0],
    durationMinutes,
    recurrence: 'none',
    weekdays: [isoWeekdayFromDate(base)],
    until: '',
  };
};

const isUserEvent = (ev) => ev && ev.source !== 'campaign';

const sortDayEvents = (list = []) =>
  list
    .slice()
    .sort((a, b) => {
      if (a.allDay && !b.allDay) return -1;
      if (!a.allDay && b.allDay) return 1;
      return String(a.startAt || '').localeCompare(String(b.startAt || ''));
    });

const formatDayChipTime = (ev) => {
  if (ev?.allDay) return 'Tag';
  return formatEventTime(ev?.startAt) || '—';
};

const MOBILE_CALENDAR_MQ = '(max-width: 640px)';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
];

const CalendarPage = ({ embedded = false }) => {
  const { events, refreshEvents, createEvent, updateEvent, deleteEvent } = usePlanner();
  const campaigns = []; // Kampagnen-Feature entfernt
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [dayContextMenu, setDayContextMenu] = useState(null); // { x, y, date, userEvents }
  const [animKey, setAnimKey] = useState(0);
  const [slideDir, setSlideDir] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm(new Date()));
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [campaignDetail, setCampaignDetail] = useState(null); // { event, campaign }
  const [previewEvent, setPreviewEvent] = useState(null);
  const [mobileDayOpen, setMobileDayOpen] = useState(false);
  const [threeWeekView, setThreeWeekView] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_CALENDAR_MQ).matches : false
  );
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });
  const monthPickerBtnRef = useRef(null);
  const monthPickerPopoverRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_CALENDAR_MQ);
    const sync = () => setThreeWeekView(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const { from, to } = getCalendarFetchRange(cursor, { threeWeeks: threeWeekView });
    refreshEvents(from.toISOString(), to.toISOString());
  }, [cursor, threeWeekView, refreshEvents]);

  // Nach Anlegen/Ändern (auch von anderer Stelle) sichtbaren Bereich neu laden
  useEffect(() => {
    return subscribeCalendarChanged(() => {
      const { from, to } = getCalendarFetchRange(cursor, { threeWeeks: threeWeekView });
      refreshEvents(from.toISOString(), to.toISOString()).catch(() => {});
    });
  }, [cursor, threeWeekView, refreshEvents]);


  const allEvents = useMemo(() => [...(events || [])], [events]);

  const days = useMemo(
    () => (threeWeekView ? buildThreeWeekCells(cursor) : buildMonthCells(cursor)),
    [cursor, threeWeekView]
  );
  const eventsByDay = useMemo(() => groupEventsByDay(allEvents), [allEvents]);
  const viewTitle = threeWeekView ? formatThreeWeekTitle(cursor) : formatMonthTitle(cursor);

  const selectedEvents = useMemo(() => {
    const key = toDateKey(selectedDay);
    return sortDayEvents(eventsByDay[key] || []);
  }, [eventsByDay, selectedDay]);

  useEffect(() => {
    if (!selectedEventId) return;
    const stillThere = selectedEvents.some((ev) => String(ev.id) === String(selectedEventId));
    if (!stillThere) setSelectedEventId(null);
  }, [selectedEvents, selectedEventId]);

  useEffect(() => {
    if (!dayContextMenu) return undefined;
    const close = () => setDayContextMenu(null);
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [dayContextMenu]);

  useEffect(() => {
    if (!monthPickerOpen) return undefined;
    const syncPos = () => {
      if (!monthPickerBtnRef.current) return;
      const r = monthPickerBtnRef.current.getBoundingClientRect();
      setPickerPos({ top: r.bottom + 6, left: r.left });
    };
    syncPos();
    const onDoc = (e) => {
      if (monthPickerBtnRef.current?.contains(e.target)) return;
      if (monthPickerPopoverRef.current?.contains(e.target)) return;
      setMonthPickerOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMonthPickerOpen(false);
    };
    window.addEventListener('resize', syncPos);
    window.addEventListener('scroll', syncPos, true);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', syncPos);
      window.removeEventListener('scroll', syncPos, true);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [monthPickerOpen]);

  const shiftView = (delta) => {
    setMonthPickerOpen(false);
    setSlideDir(delta);
    setAnimKey((k) => k + 1);
    setCursor((prev) => {
      if (threeWeekView) {
        const next = new Date(prev.getFullYear(), prev.getMonth(), prev.getDate());
        next.setDate(next.getDate() + delta * 7);
        return next;
      }
      return new Date(prev.getFullYear(), prev.getMonth() + delta, 1);
    });
  };

  const goToMonth = (year, monthIndex) => {
    const prev = cursor;
    const safeDay = Math.min(
      prev.getDate(),
      new Date(year, monthIndex + 1, 0).getDate()
    );
    const next = threeWeekView
      ? new Date(year, monthIndex, safeDay)
      : new Date(year, monthIndex, 1);
    const dir =
      year > prev.getFullYear() ||
      (year === prev.getFullYear() && monthIndex > prev.getMonth())
        ? 1
        : -1;
    setSlideDir(dir);
    setAnimKey((k) => k + 1);
    setCursor(next);
    if (threeWeekView) setSelectedDay(next);
    setMonthPickerOpen(false);
  };

  const refreshVisibleRange = async () => {
    const { from, to } = getCalendarFetchRange(cursor, { threeWeeks: threeWeekView });
    await refreshEvents(from.toISOString(), to.toISOString());
  };

  const openCreate = (day = selectedDay) => {
    setDayContextMenu(null);
    setEditingId(null);
    setSelectedEventId(null);
    const base = day ? new Date(day) : new Date();
    const now = new Date();
    if (sameDay(base, now)) {
      base.setHours(now.getHours(), 0, 0, 0);
    } else {
      base.setHours(9, 0, 0, 0);
    }
    setForm(emptyForm(base));
    setModalOpen(true);
  };

  const closeEventModal = () => {
    if (saving || deletingEvent) return;
    setDeleteConfirmOpen(false);
    setModalOpen(false);
    setSelectedEventId(null);
    setPreviewEvent(null);
  };

  const closeCampaignDetail = () => {
    setCampaignDetail(null);
    setSelectedEventId(null);
  };

  const closePreview = () => {
    setPreviewEvent(null);
  };

  /** Vorschau: Kampagnen-Detail oder eigener Termin (ohne Edit-Formular). */
  const openPreview = (ev) => {
    setDayContextMenu(null);
    if (!ev) return;
    // Niemals Edit-Modal gleichzeitig zur Vorschau
    setModalOpen(false);
    setEditingId(null);
    setSelectedEventId(ev.id);
    if (ev.source === 'campaign') {
      const id = Number(ev.campaignId);
      const campaign = Number.isFinite(id)
        ? campaigns.find((c) => Number(c.id) === id) || null
        : null;

      const byId = new Map();
      const addMember = (row) => {
        if (!row) return;
        const mid = Number(row.id);
        if (!Number.isFinite(mid) || mid <= 0) return;
        if (!byId.has(mid)) byId.set(mid, row);
      };

      if (Array.isArray(ev.campaignIds)) {
        ev.campaignIds.forEach((cid) => {
          addMember(campaigns.find((c) => Number(c.id) === Number(cid)));
        });
      }

      if (Array.isArray(ev.members)) {
        ev.members.forEach((m) => {
          const live = campaigns.find((c) => Number(c.id) === Number(m.id));
          addMember(live || m);
        });
      }

      const batchKey = ev.batchId
        || campaign?.batch_id
        || (Array.from(byId.values()).find((m) => m.batch_id)?.batch_id);
      if (batchKey && !String(batchKey).startsWith('soft:')) {
        campaigns.forEach((c) => {
          if (String(c.batch_id || '') === String(batchKey)) addMember(c);
        });
      }

      if (byId.size < 2 && campaign) {
        const start = parseDate(campaign.sent_at || campaign.created_at || ev.startAt);
        const templateKey =
          campaign.template_id != null
            ? String(campaign.template_id)
            : String(campaign.template_name || '');
        if (start && templateKey) {
          const startMin = start.getTime() - (start.getSeconds() * 1000) - start.getMilliseconds();
          campaigns.forEach((c) => {
            if (c.batch_id) return;
            const cTemplate =
              c.template_id != null ? String(c.template_id) : String(c.template_name || '');
            if (cTemplate !== templateKey) return;
            const cStart = parseDate(c.sent_at || c.created_at);
            if (!cStart) return;
            const cMin = cStart.getTime() - (cStart.getSeconds() * 1000) - cStart.getMilliseconds();
            if (cMin === startMin) addMember(c);
          });
        }
      }

      if (campaign) addMember(campaign);

      const members = Array.from(byId.values()).sort((a, b) => Number(a.id) - Number(b.id));
      setPreviewEvent(null);
      setCampaignDetail({
        event: {
          ...ev,
          isBatch: !!ev.isBatch || members.length > 1 || (Array.isArray(ev.campaignIds) && ev.campaignIds.length > 1),
          batchId: batchKey ? String(batchKey) : (ev.batchId || null),
          campaignIds: members.map((m) => Number(m.id)),
          members,
        },
        campaign: members[0] || campaign,
        members,
      });
      return;
    }
    setCampaignDetail(null);
    setPreviewEvent(ev);
  };

  const openEdit = (ev) => {
    setDayContextMenu(null);
    setPreviewEvent(null);
    if (ev.source === 'campaign') {
      openPreview(ev);
      return;
    }
    const seriesId = resolveSeriesId(ev);
    setEditingId(seriesId);
    const start = parseDate(ev.startAt) || new Date();
    const end = parseDate(ev.endAt) || new Date(start.getTime() + 3600000);
    const isAllDay = !!ev.allDay;
    const durationMinutes = isAllDay
      ? ALL_DAY_MINUTES
      : Number(ev.durationMinutes) > 0
        ? Number(ev.durationMinutes)
        : Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000) || 60);
    const weekdays =
      Array.isArray(ev.weekdays) && ev.weekdays.length
        ? ev.weekdays.map(Number)
        : [isoWeekdayFromDate(start)];
    const allDayBounds = isAllDay ? toAllDayBounds(start) : null;
    const startLocal = isAllDay ? allDayBounds.startAt : toLocalInput(start);
    setForm({
      title: ev.title || '',
      description: ev.description || '',
      location: ev.location || '',
      startAt: startLocal,
      endAt: isAllDay ? allDayBounds.endAt : toLocalInput(end),
      allDay: isAllDay,
      color: ev.color || EVENT_COLORS[0],
      durationMinutes,
      recurrence: ev.recurrence || 'none',
      weekdays,
      until: ev.until
        ? (() => {
            const day = String(ev.until).slice(0, 10);
            const d = new Date(`${day}T23:59:00`);
            return Number.isNaN(d.getTime()) ? day : toLocalInput(d);
          })()
        : '',
    });
    setModalOpen(true);
  };

  const openDayContextMenu = (e, date, dayEvents, focusEvent = null) => {
    e.preventDefault();
    e.stopPropagation();
    const userEvents = (dayEvents || []).filter(isUserEvent);
    const menuW = 220;
    const itemCount = focusEvent
      ? (isUserEvent(focusEvent) ? 2 : 1)
      : 1 + Math.min(userEvents.length, 6);
    const menuH = 48 + itemCount * 36;
    const x = Math.min(e.clientX, window.innerWidth - menuW - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuH - 8);
    setSelectedDay(date);
    setMobileDayOpen(true);
    if (focusEvent) setSelectedEventId(focusEvent.id);
    setDayContextMenu({
      x: Math.max(8, x),
      y: Math.max(8, y),
      date,
      userEvents,
      focusEvent: focusEvent || null,
    });
  };

  const requestDeleteEvent = (ev) => {
    if (!isUserEvent(ev)) {
      toast.error('Kampagnen-Termine können hier nicht gelöscht werden');
      return;
    }
    setDayContextMenu(null);
    setSelectedEventId(ev.id);
    setEditingId(resolveSeriesId(ev));
    setForm((p) => ({
      ...p,
      title: ev.title || '',
      recurrence: ev.recurrence || 'none',
    }));
    setDeleteConfirmOpen(true);
  };

  const tryOpenPreviewForDay = (date, dayEvents) => {
    const userEvents = (dayEvents || []).filter(isUserEvent);
    if (!userEvents.length) return;
    const selected = userEvents.find((ev) => String(ev.id) === String(selectedEventId));
    if (selected) {
      openPreview(selected);
      return;
    }
    if (userEvents.length === 1) {
      setSelectedEventId(userEvents[0].id);
      openPreview(userEvents[0]);
      return;
    }
    setSelectedDay(date);
    setMobileDayOpen(true);
    toast('Termin in der Liste auswählen', { icon: '📅', duration: 2800 });
  };

  const updateFormStart = (startAt) => {
    setForm((p) => {
      const next = { ...p, startAt };
      next.endAt = applyDurationToEnd(startAt, p.durationMinutes);
      const start = new Date(startAt);
      if (!Number.isNaN(start.getTime()) && p.recurrence === 'weekly' && !p.weekdays?.length) {
        next.weekdays = [isoWeekdayFromDate(start)];
      }
      return next;
    });
  };

  const updateFormDuration = (durationMinutes) => {
    const mins = Math.max(15, Number(durationMinutes) || 60);
    setForm((p) => ({
      ...p,
      durationMinutes: mins,
      endAt: applyDurationToEnd(p.startAt, mins),
    }));
  };

  const openCampaignConversation = () => {
    // Kampagnen-Feature entfernt
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) {
      toast.error('Titel fehlt');
      return;
    }
    if (!form.startAt || Number.isNaN(new Date(form.startAt).getTime())) {
      toast.error('Startzeit fehlt');
      return;
    }
    if (form.recurrence === 'weekly' && (!form.weekdays || form.weekdays.length === 0)) {
      toast.error('Bitte mindestens einen Wochentag wählen');
      return;
    }

    const startIso = form.allDay
      ? new Date(toAllDayBounds(form.startAt).startAt).toISOString()
      : new Date(form.startAt).toISOString();
    const endIso = form.allDay
      ? new Date(toAllDayBounds(form.startAt).endAt).toISOString()
      : form.endAt
        ? new Date(form.endAt).toISOString()
        : new Date(
            new Date(form.startAt).getTime() +
              Math.max(15, Number(form.durationMinutes) || 60) * 60 * 1000
          ).toISOString();

    if (!form.allDay) {
      const conflicts = findBusyConflicts(events || [], startIso, endIso, {
        excludeId: editingId,
        allDay: false,
      });
      if (conflicts.length > 0) {
        const first = conflicts[0];
        const when = formatEventTime(first.startAt);
        toast.error(
          when
            ? `Diese Uhrzeit ist bereits belegt (${when} · ${first.title || 'Termin'})`
            : 'Diese Uhrzeit ist bereits belegt – du bist dort ausgebucht'
        );
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description,
        location: form.location,
        startAt: startIso,
        endAt: endIso,
        allDay: !!form.allDay,
        color: form.color,
        durationMinutes: form.allDay
          ? ALL_DAY_MINUTES
          : Number(form.durationMinutes) || 60,
        recurrence: form.recurrence || 'none',
        weekdays: form.recurrence === 'weekly' ? form.weekdays : [],
        until: form.recurrence !== 'none' && form.until
          ? String(form.until).slice(0, 10)
          : null,
      };
      if (editingId) {
        await updateEvent(editingId, payload);
        toast.success(
          form.recurrence !== 'none' ? 'Serie aktualisiert' : 'Termin aktualisiert'
        );
      } else {
        await createEvent(payload);
        toast.success(form.recurrence !== 'none' ? 'Serie angelegt' : 'Termin angelegt');
      }
      setModalOpen(false);
      setSelectedEventId(null);
      await refreshVisibleRange();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteEvent = async () => {
    if (!editingId || deletingEvent) return;
    const isSeries = form.recurrence && form.recurrence !== 'none';
    setDeletingEvent(true);
    try {
      await deleteEvent(editingId);
      toast.success(isSeries ? 'Serie gelöscht' : 'Termin gelöscht');
      setDeleteConfirmOpen(false);
      setModalOpen(false);
      setSelectedEventId(null);
      await refreshVisibleRange();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Löschen fehlgeschlagen');
    } finally {
      setDeletingEvent(false);
    }
  };

  const today = new Date();

  return (
    <div
      className={
        embedded
          ? 'h-full min-h-0 overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex flex-col'
          : 'h-full min-h-0 overflow-hidden flex flex-col'
      }
    >
      <div
        className={
          embedded
            ? 'calendar-page calendar-page--embedded flex-1 min-h-0 px-2.5 sm:px-3 py-2.5 flex flex-col'
            : 'calendar-page calendar-page--full flex-1 min-h-0 w-full max-w-none px-3 sm:px-4 lg:px-5 py-3 sm:py-4 flex flex-col'
        }
      >
        {embedded ? null : (
          <div className="mb-3 sm:mb-4 flex-shrink-0">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-neutral-900 dark:text-white">
              Kalender
            </h1>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Meetings, Fristen und eigene Termine.
            </p>
          </div>
        )}

        <div
          className={
            embedded
              ? 'calendar-layout flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_280px] gap-3'
              : 'calendar-layout flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4 w-full'
          }
        >
          <div className={`calendar-shell ${embedded ? 'calendar-shell--embedded' : 'calendar-shell--full'}`}>
            <div className="flex items-center justify-between gap-2 sm:gap-3 mb-1.5 sm:mb-2 flex-shrink-0">
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                <button
                  type="button"
                  className="calendar-nav-btn"
                  onClick={() => shiftView(-1)}
                  aria-label={threeWeekView ? 'Vorherige Woche' : 'Vorheriger Monat'}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  className="calendar-nav-btn"
                  onClick={() => shiftView(1)}
                  aria-label={threeWeekView ? 'Nächste Woche' : 'Nächster Monat'}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <div className="relative min-w-0">
                  <button
                    type="button"
                    ref={monthPickerBtnRef}
                    key={animKey}
                    className={`calendar-month-title calendar-month-title-btn calendar-month-title--${
                      slideDir >= 0 ? 'next' : 'prev'
                    } truncate ${monthPickerOpen ? 'is-open' : ''}`}
                    onClick={() => {
                      setPickerYear(cursor.getFullYear());
                      setMonthPickerOpen((open) => !open);
                    }}
                    aria-expanded={monthPickerOpen}
                    aria-haspopup="dialog"
                    title="Monat auswählen"
                  >
                    {viewTitle}
                  </button>
                </div>
              </div>
              <button
                type="button"
                className="btn-secondary !py-1.5 !px-2.5 sm:!px-3 text-xs flex-shrink-0"
                onClick={() => {
                  const now = new Date();
                  setMonthPickerOpen(false);
                  setSlideDir(0);
                  setAnimKey((k) => k + 1);
                  setCursor(now);
                  setSelectedDay(now);
                  setMobileDayOpen(true);
                }}
              >
                Heute
              </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1 flex-shrink-0">
              {WEEKDAYS.map((d) => (
                <div key={d} className="text-center text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide sm:tracking-wider text-neutral-400 py-0.5">
                  {d}
                </div>
              ))}
            </div>

            <div
              key={`grid-${animKey}`}
              className={`calendar-grid calendar-grid--fill calendar-grid--${slideDir >= 0 ? 'next' : 'prev'} ${
                threeWeekView ? 'calendar-grid--three-weeks' : ''
              }`}
            >
              {days.map(({ date, inMonth }) => {
                const key = toDateKey(date);
                const dayEvents = sortDayEvents(eventsByDay[key] || []);
                const isSelected = sameDay(date, selectedDay);
                const isToday = sameDay(date, today);
                const maxVisible = 3;
                const visibleEvents = dayEvents.slice(0, maxVisible);
                const hiddenCount = Math.max(0, dayEvents.length - visibleEvents.length);
                return (
                  <div
                    key={key}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedDay(date);
                      setMobileDayOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedDay(date);
                        setMobileDayOpen(true);
                      }
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      setSelectedDay(date);
                      setMobileDayOpen(true);
                      tryOpenPreviewForDay(date, dayEvents);
                    }}
                    onContextMenu={(e) => openDayContextMenu(e, date, dayEvents)}
                    className={`calendar-day ${inMonth ? '' : 'is-muted'} ${isSelected ? 'is-selected' : ''} ${isToday ? 'is-today' : ''}`}
                  >
                    <div className="calendar-day__head">
                      <span className="calendar-day__num">{date.getDate()}</span>
                      {dayEvents.length > 0 ? (
                        <span className="calendar-day__count">{dayEvents.length}</span>
                      ) : null}
                    </div>
                    <div className="calendar-day__events">
                      {visibleEvents.map((ev) => {
                        const isActive = String(ev.id) === String(selectedEventId) && sameDay(date, selectedDay);
                        const chipColor = resolveCalendarEventColor(ev);
                        return (
                          <div
                            key={ev.id}
                            role="button"
                            tabIndex={-1}
                            title={`${formatDayChipTime(ev)} · ${ev.title || 'Termin'}`}
                            className={`calendar-day__event ${isActive ? 'is-active' : ''}`}
                            style={{
                              backgroundColor: eventColorRgba(chipColor, isActive ? 0.92 : 0.82),
                              borderColor: eventColorRgba(chipColor, 1),
                              color: '#ffffff',
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedDay(date);
                              setSelectedEventId(ev.id);
                              // Vorschau öffnen — Bottom-Sheet nicht aufklappen
                              openPreview(ev);
                            }}
                            onDoubleClick={(e) => {
                              // Doppelklick soll nicht mehr direkt bearbeiten
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onContextMenu={(e) => {
                              e.stopPropagation();
                              openDayContextMenu(e, date, dayEvents, ev);
                            }}
                          >
                            <span className="calendar-day__event-time">{formatDayChipTime(ev)}</span>
                            <span className="calendar-day__event-title">{ev.title || 'Termin'}</span>
                          </div>
                        );
                      })}
                      {hiddenCount > 0 ? (
                        <span className="calendar-day__more">+{hiddenCount} weitere</span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="calendar-legend flex items-center gap-x-3 gap-y-1 text-[10px] sm:text-[11px] text-neutral-500 mt-1 sm:mt-1.5 flex-shrink-0 flex-wrap">
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: USER_EVENT_COLOR }} />
                {USER_EVENT_LABEL}
              </span>
            </div>
          </div>

          <aside
            className={`calendar-side calendar-side--embedded ${mobileDayOpen ? 'is-open' : ''}`}
          >
            <button
              type="button"
              className="calendar-side__sheet-toggle xl:hidden"
              onClick={() => setMobileDayOpen((v) => !v)}
              aria-expanded={mobileDayOpen}
            >
              {mobileDayOpen ? (
                <span className="w-full text-center text-[10px] font-medium text-neutral-500 pt-1">
                  Zuklappen
                </span>
              ) : (
                <>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="block text-[11px] font-semibold text-neutral-900 dark:text-white truncate">
                      {selectedDay.toLocaleDateString('de-DE', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      })}
                    </span>
                    <span className="block text-[10px] text-neutral-500">
                      {selectedEvents.length === 0
                        ? 'Keine Termine'
                        : `${selectedEvents.length} Termin${selectedEvents.length === 1 ? '' : 'e'}`}
                    </span>
                  </span>
                  <span className="text-[10px] font-medium text-primary-600 dark:text-primary-400 flex-shrink-0">
                    Öffnen
                  </span>
                </>
              )}
            </button>

            <div className={`calendar-side__body ${!mobileDayOpen ? 'calendar-side__body--collapsed' : ''}`}>
              <div className="calendar-side__header">
                <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold hidden xl:block">Ausgewählt</p>
                <h3 className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-white mt-0 xl:mt-1">
                  <span className="xl:hidden">
                    {selectedDay.toLocaleDateString('de-DE', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                    })}
                  </span>
                  <span className="hidden xl:inline">
                    {selectedDay.toLocaleDateString('de-DE', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })}
                  </span>
                </h3>
                <button
                  type="button"
                  className="btn-secondary !py-1.5 text-xs mt-2 sm:mt-3 w-full justify-center"
                  onClick={() => openCreate(selectedDay)}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Termin hinzufügen
                </button>
              </div>

              <div className="calendar-side__list">
                {selectedEvents.length === 0 ? (
                  <div className="calendar-side__empty">
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">Keine Termine</p>
                  </div>
                ) : (
                  selectedEvents.map((ev, idx) => {
                    const isActive = String(ev.id) === String(selectedEventId);
                    const chipColor = resolveCalendarEventColor(ev);
                    return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedEventId(ev.id);
                        openPreview(ev);
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                      }}
                      onContextMenu={(e) => {
                        openDayContextMenu(e, selectedDay, selectedEvents, ev);
                      }}
                      className={`calendar-event-card ${isActive ? 'is-selected' : ''}`}
                      style={{ animationDelay: `${idx * 45}ms` }}
                    >
                      <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: chipColor }}
                          aria-hidden
                        />
                        {ev.source === 'campaign' ? (
                          <span className="text-[10px] font-medium text-neutral-500 truncate max-w-full">
                            {ev.kind === 'followup'
                              ? `Follow-up${ev.followupNumber ? ` ${ev.followupNumber}` : ''}`
                              : 'Kampagne'}
                            {ev.campaignLabel || ev.templateName
                              ? ` · ${ev.campaignLabel || ev.templateName}`
                              : ''}
                            {ev.isBatch && Array.isArray(ev.members) && ev.members.length > 1
                              ? ` · ${ev.members.length}`
                              : ''}
                          </span>
                        ) : (
                          <span className="text-[10px] font-medium text-neutral-500">Termin</span>
                        )}
                        {recurrenceLabel(ev) ? (
                          <span className="text-[10px] font-medium text-primary-600 dark:text-primary-400">
                            {recurrenceLabel(ev)}
                          </span>
                        ) : null}
                      </div>
                      <p className="font-medium text-sm text-neutral-900 dark:text-white truncate">{ev.title}</p>
                      {ev.isBatch && ev.description ? (
                        <p className="text-[11px] text-neutral-500 mt-0.5 truncate">
                          {String(ev.description).split('\n')[0]}
                        </p>
                      ) : null}
                      <p className="text-[11px] text-neutral-500 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {ev.allDay
                          ? 'Ganztägig'
                          : `${formatTime(ev.startAt)}${ev.endAt ? ` – ${formatTime(ev.endAt)}` : ''}`}
                      </p>
                      {ev.location ? (
                        <p className="text-[11px] text-neutral-500 mt-0.5 flex items-center gap-1 truncate">
                          <MapPin className="w-3 h-3" />
                          {ev.location}
                        </p>
                      ) : null}
                    </button>
                    );
                  })
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {dayContextMenu && createPortal(
        <div
          className="fixed z-[210] min-w-[12rem] max-w-[16rem] rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl py-1 scale-in"
          style={{ left: dayContextMenu.x, top: dayContextMenu.y }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="px-3 py-2 border-b border-neutral-100 dark:border-neutral-800">
            <p className="text-xs font-medium text-neutral-800 dark:text-neutral-100">
              {dayContextMenu.date.toLocaleDateString('de-DE', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })}
            </p>
          </div>
          {dayContextMenu.focusEvent ? (
            <>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                onClick={() => openPreview(dayContextMenu.focusEvent)}
              >
                <Eye className="w-4 h-4 text-neutral-400" />
                Vorschau
              </button>
              {isUserEvent(dayContextMenu.focusEvent) ? (
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  onClick={() => openEdit(dayContextMenu.focusEvent)}
                >
                  <Pencil className="w-4 h-4 text-neutral-400" />
                  Bearbeiten
                </button>
              ) : null}
              {isUserEvent(dayContextMenu.focusEvent) ? (
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                  onClick={() => requestDeleteEvent(dayContextMenu.focusEvent)}
                >
                  <Trash2 className="w-4 h-4" />
                  Löschen
                </button>
              ) : null}
            </>
          ) : (
            <>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                onClick={() => openCreate(dayContextMenu.date)}
              >
                <Plus className="w-4 h-4 text-neutral-400" />
                Neuer Termin
              </button>
              {dayContextMenu.userEvents?.length ? (
                <>
                  <div className="my-1 border-t border-neutral-100 dark:border-neutral-800" />
                  {dayContextMenu.userEvents.slice(0, 6).map((ev) => (
                    <button
                      key={ev.id}
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                      onClick={() => requestDeleteEvent(ev)}
                      title={ev.title}
                    >
                      <Trash2 className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">
                        {dayContextMenu.userEvents.length === 1
                          ? 'Termin löschen'
                          : `„${ev.title || 'Termin'}“ löschen`}
                      </span>
                    </button>
                  ))}
                </>
              ) : null}
            </>
          )}
        </div>,
        document.body
      )}

      {modalOpen && createPortal(
        <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-neutral-900/55 backdrop-blur-sm" onClick={closeEventModal} />
          <form
            onSubmit={handleSave}
            className="relative w-full max-w-md bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-xl overflow-hidden calendar-modal"
          >
            <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-neutral-100 dark:border-neutral-800">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                  {editingId ? 'Termin bearbeiten' : 'Neuer Termin'}
                </h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Einmalig oder wiederkehrend · mit Dauer
                </p>
              </div>
              <button
                type="button"
                className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={closeEventModal}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
              <div>
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1">Titel</label>
                <input
                  className="input-field"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="z. B. Weekly Sync"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1">Start</label>
                <DateTimePicker
                  value={form.startAt ? new Date(form.startAt).toISOString() : null}
                  placeholder="Datum & Uhrzeit wählen"
                  panelZIndex={240}
                  disableWeekends={false}
                  onChange={(next) => {
                    if (!next) return;
                    const d = new Date(next);
                    if (Number.isNaN(d.getTime())) return;
                    updateFormStart(toLocalInput(d));
                  }}
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  checked={form.allDay}
                  onChange={(e) => {
                    const allDay = e.target.checked;
                    setForm((p) => {
                      if (allDay) {
                        const bounds = toAllDayBounds(p.startAt);
                        return {
                          ...p,
                          allDay: true,
                          durationMinutes: ALL_DAY_MINUTES,
                          startAt: bounds.startAt,
                          endAt: bounds.endAt,
                        };
                      }
                      const durationMinutes =
                        p.durationMinutes < ALL_DAY_MINUTES ? p.durationMinutes : 60;
                      const start = new Date(p.startAt);
                      if (!Number.isNaN(start.getTime()) && start.getHours() === 0 && start.getMinutes() === 0) {
                        start.setHours(9, 0, 0, 0);
                      }
                      const startAt = toLocalInput(start);
                      return {
                        ...p,
                        allDay: false,
                        durationMinutes,
                        startAt,
                        endAt: applyDurationToEnd(startAt, durationMinutes),
                      };
                    });
                  }}
                  className="rounded border-neutral-300 text-primary-600"
                />
                Ganztägig
              </label>
              {!form.allDay ? (
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1">
                    Dauer
                  </label>
                  <SelectField
                    className="input-field"
                    value={form.durationMinutes}
                    onChange={(e) => updateFormDuration(e.target.value)}
                  >
                    {DURATION_PRESETS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {!DURATION_PRESETS.some((o) => o.value === Number(form.durationMinutes)) ? (
                      <option value={form.durationMinutes}>{form.durationMinutes} Min.</option>
                    ) : null}
                  </SelectField>
                  <p className="text-[11px] text-neutral-400 mt-1">
                    Ende: {form.endAt ? formatTime(new Date(form.endAt).toISOString()) : '—'}
                  </p>
                </div>
              ) : null}
              <div>
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1">
                  Wiederholung
                </label>
                <SelectField
                  className="input-field"
                  value={form.recurrence}
                  onChange={(e) => {
                    const recurrence = e.target.value;
                    setForm((p) => {
                      const start = new Date(p.startAt);
                      const weekdays =
                        recurrence === 'weekly'
                          ? (p.weekdays?.length ? p.weekdays : [isoWeekdayFromDate(start)])
                          : p.weekdays;
                      return { ...p, recurrence, weekdays };
                    });
                  }}
                >
                  <option value="none">Keine (einmalig)</option>
                  <option value="daily">Täglich</option>
                  <option value="weekly">Wöchentlich (bestimmte Tage)</option>
                  <option value="monthly">Monatlich (gleiches Datum)</option>
                </SelectField>
                {form.recurrence === 'monthly' ? (
                  <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1.5 leading-relaxed">
                    {(() => {
                      const start = new Date(form.startAt);
                      const day = Number.isNaN(start.getTime()) ? null : start.getDate();
                      if (!day) {
                        return 'Der Termin wiederholt sich jeden Monat am gleichen Kalendertag wie das Startdatum (z. B. immer am 15.).';
                      }
                      return (
                        <>
                          Wiederholt sich <span className="font-medium text-neutral-700 dark:text-neutral-200">jeden Monat am {day}.</span>
                          {' '}
                          Beispiel: Start am {start.toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })}
                          {' → '}
                          nächste Termine am {day}. des Folgemonats usw.
                          {day >= 29 ? (
                            <> Monate ohne diesen Tag (z. B. Februar) werden übersprungen.</>
                          ) : null}
                        </>
                      );
                    })()}
                  </p>
                ) : null}
              </div>
              {form.recurrence === 'weekly' ? (
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1.5">
                    An diesen Tagen
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {WEEKDAY_OPTIONS.map((wd) => {
                      const active = form.weekdays?.includes(wd.value);
                      return (
                        <button
                          key={wd.value}
                          type="button"
                          onClick={() => {
                            setForm((p) => {
                              const set = new Set(p.weekdays || []);
                              if (set.has(wd.value)) set.delete(wd.value);
                              else set.add(wd.value);
                              return { ...p, weekdays: Array.from(set).sort((a, b) => a - b) };
                            });
                          }}
                          className={`w-9 h-9 rounded-lg text-xs font-semibold border transition-colors ${
                            active
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                          }`}
                        >
                          {wd.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {form.recurrence !== 'none' ? (
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1">
                    Endet am (optional)
                  </label>
                  <DateTimePicker
                    value={
                      form.until
                        ? (() => {
                            const raw = String(form.until);
                            if (raw.includes('T') || raw.includes(' ')) {
                              const d = new Date(raw);
                              return Number.isNaN(d.getTime()) ? null : d.toISOString();
                            }
                            const d = new Date(`${raw.slice(0, 10)}T23:59:00`);
                            return Number.isNaN(d.getTime()) ? null : d.toISOString();
                          })()
                        : null
                    }
                    placeholder="Datum & Uhrzeit wählen"
                    panelZIndex={240}
                    disableWeekends={false}
                    minDate={form.startAt ? new Date(form.startAt) : null}
                    onChange={(next) => {
                      if (!next) {
                        setForm((p) => ({ ...p, until: '' }));
                        return;
                      }
                      const d = new Date(next);
                      if (Number.isNaN(d.getTime())) return;
                      setForm((p) => ({ ...p, until: toLocalInput(d) }));
                    }}
                  />
                </div>
              ) : null}
              <div>
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1">Ort</label>
                <input
                  className="input-field"
                  value={form.location}
                  onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                  placeholder="Zoom / Büro / …"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1">Notiz</label>
                <textarea
                  className="input-field resize-none"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Agenda, Teilnehmer…"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1.5">Farbe</label>
                <div className="flex flex-wrap items-center gap-2">
                  {EVENT_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, color: c }))}
                      className={`w-6 h-6 rounded-full transition-transform hover:scale-110 ${
                        form.color === c ? 'ring-2 ring-offset-2 ring-neutral-800 dark:ring-white dark:ring-offset-neutral-900' : ''
                      }`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              {editingId ? (
                <button
                  type="button"
                  disabled={saving || deletingEvent}
                  className="inline-flex items-center text-sm text-danger-600 hover:text-danger-700"
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Löschen
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button type="button" className="btn-secondary !py-2" disabled={saving} onClick={closeEventModal}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary !py-2" disabled={saving}>
                  {saving ? 'Speichern…' : 'Speichern'}
                </button>
              </div>
            </div>
          </form>
        </div>,
        document.body
      )}

      {deleteConfirmOpen && createPortal(
        <div className="fixed inset-0 z-[230] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            onClick={() => !deletingEvent && setDeleteConfirmOpen(false)}
          />
          <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden border border-neutral-200 dark:border-neutral-800 scale-in">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
                    {form.recurrence && form.recurrence !== 'none'
                      ? 'Terminserie löschen?'
                      : 'Termin löschen?'}
                  </h3>
                  <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400">
                    {form.recurrence && form.recurrence !== 'none' ? (
                      <>
                        Möchtest du die gesamte Serie
                        {form.title.trim() ? (
                          <>
                            {' '}
                            <span className="font-medium text-neutral-800 dark:text-neutral-200">
                              „{form.title.trim()}“
                            </span>
                          </>
                        ) : null}{' '}
                        wirklich löschen? Alle Termine der Serie werden entfernt.
                      </>
                    ) : (
                      <>
                        Möchtest du den Termin
                        {form.title.trim() ? (
                          <>
                            {' '}
                            <span className="font-medium text-neutral-800 dark:text-neutral-200">
                              „{form.title.trim()}“
                            </span>
                          </>
                        ) : null}{' '}
                        wirklich löschen? Das lässt sich nicht rückgängig machen.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
              <button
                type="button"
                disabled={deletingEvent}
                onClick={confirmDeleteEvent}
                className="inline-flex items-center justify-center rounded-lg bg-danger-600 hover:bg-danger-700 text-white text-sm font-medium px-3.5 py-2 disabled:opacity-60"
              >
                {deletingEvent ? 'Löschen…' : 'Löschen'}
              </button>
              <button
                type="button"
                disabled={deletingEvent}
                onClick={() => setDeleteConfirmOpen(false)}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800 disabled:opacity-60"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {previewEvent && createPortal(
        <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/55 backdrop-blur-sm"
            onClick={closePreview}
          />
          <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-xl overflow-hidden calendar-modal">
            {(() => {
              const ev = previewEvent;
              const chipColor = resolveCalendarEventColor(ev);
              const recur = recurrenceLabel(ev);
              return (
                <>
                  <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-neutral-100 dark:border-neutral-800">
                    <div className="min-w-0">
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md"
                        style={{
                          color: chipColor,
                          backgroundColor: eventColorRgba(chipColor, 0.14),
                        }}
                      >
                        <Eye className="w-3 h-3" />
                        Termin
                      </span>
                      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mt-2 break-words whitespace-normal leading-snug">
                        {ev.title || 'Termin'}
                      </h3>
                      <p className="text-xs text-neutral-500 mt-0.5">Termindetails</p>
                    </div>
                    <button
                      type="button"
                      className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                      onClick={closePreview}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="px-5 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
                    <div className="flex items-start gap-2.5 text-sm text-neutral-700 dark:text-neutral-200">
                      <Clock className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                      <div>
                        {ev.allDay ? (
                          <p>Ganztägig</p>
                        ) : (
                          <p>
                            {formatTime(ev.startAt)}
                            {ev.endAt ? ` – ${formatTime(ev.endAt)}` : ''}
                          </p>
                        )}
                        <p className="text-xs text-neutral-500 mt-0.5">
                          {(parseDate(ev.startAt) || new Date()).toLocaleDateString('de-DE', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </p>
                        {recur ? (
                          <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">{recur}</p>
                        ) : null}
                      </div>
                    </div>
                    {ev.location ? (
                      <div className="flex items-start gap-2.5 text-sm text-neutral-700 dark:text-neutral-200">
                        <MapPin className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                        <p className="break-words">{ev.location}</p>
                      </div>
                    ) : null}
                    {ev.description ? (
                      <div className="rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-950/40 px-3 py-2.5">
                        <p className="text-[11px] font-medium text-neutral-500 mb-1">Beschreibung</p>
                        <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap break-words">
                          {ev.description}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-neutral-500">Keine Beschreibung</p>
                    )}
                  </div>
                  <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex flex-row-reverse flex-wrap gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-3.5 py-2"
                      onClick={() => openEdit(ev)}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-sm font-medium px-3.5 py-2 hover:bg-white dark:hover:bg-neutral-800"
                      onClick={closePreview}
                    >
                      Schließen
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>,
        document.body
      )}

      {campaignDetail && createPortal(
        <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-neutral-900/55 backdrop-blur-sm"
            onClick={closeCampaignDetail}
          />
          <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-xl overflow-hidden calendar-modal">
            {(() => {
              const ev = campaignDetail.event;
              const campaign = campaignDetail.campaign;
              const rawMembers = Array.isArray(campaignDetail.members) && campaignDetail.members.length
                ? campaignDetail.members
                : (campaign ? [campaign] : []);
              const members = filterCalendarEventMembers(rawMembers, { kind: ev.kind });
              const isFollowup = ev.kind === 'followup';
              const isBatch = members.length > 1;
              const singleMember = members.length === 1 ? members[0] : null;
              const typeLabel = isFollowup ? CAMPAIGN_FOLLOWUP_LABEL : CAMPAIGN_START_LABEL;
              const typeColor = isFollowup ? CAMPAIGN_FOLLOWUP_COLOR : CAMPAIGN_START_COLOR;
              const memberTemplate =
                String(campaign?.template_name || '').trim() ||
                String(members.find((m) => m.template_name)?.template_name || '').trim();
              const campaignName =
                memberTemplate ||
                String(ev.campaignLabel || '').trim() ||
                String(ev.templateName || '').trim() ||
                (singleMember
                  ? singleMember.contact_name ||
                    singleMember.contact_email ||
                    `Kampagne #${singleMember.id}`
                  : '') ||
                String(ev.title || '').trim() ||
                'Kampagne';
              const followupTemplateName = (() => {
                if (!isFollowup) return '';
                const explicit = String(ev.followupTemplateName || '').trim();
                if (explicit && explicit !== campaignName) return explicit;
                const fromTemplate = String(ev.templateName || '').trim();
                if (fromTemplate && fromTemplate !== campaignName) return fromTemplate;
                const fromLabel = String(ev.campaignLabel || '').trim();
                if (fromLabel && fromLabel !== campaignName) return fromLabel;
                return '';
              })();
              const followupNo =
                ev.followupNumber != null
                  ? Number(ev.followupNumber)
                  : isFollowup
                    ? Number(String(ev.title || '').match(/Follow-up\s+(\d+)/i)?.[1]) || null
                    : null;
              const descLines = String(ev.description || '')
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean)
                .filter((l) => !/^Kampagne\s*:/i.test(l) && !/^Follow-up-Vorlage\s*:/i.test(l));
              const repliedCount = members.filter((m) => Number(m.reply_received) === 1 || Number(m.auto_reply_received) === 1).length;

              return (
                <>
                  <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-neutral-100 dark:border-neutral-800">
                    <div className="min-w-0">
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md"
                        style={{
                          color: typeColor,
                          backgroundColor: `${typeColor}18`,
                        }}
                      >
                        {isBatch ? <Users className="w-3 h-3" /> : <Send className="w-3 h-3" />}
                        {typeLabel}
                        {followupNo ? ` ${followupNo}` : ''}
                      </span>
                      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mt-2 break-words whitespace-normal leading-snug">
                        {campaignName}
                      </h3>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        {isBatch
                          ? `${members.length} Empfänger in dieser Kampagne`
                          : singleMember
                            ? `Empfänger: ${
                                singleMember.contact_name ||
                                singleMember.contact_email ||
                                `#${singleMember.id}`
                              }`
                            : 'Termindetails'}
                        {campaign?.id ? ` · #${campaign.id}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex-shrink-0"
                      onClick={closeCampaignDetail}
                      aria-label="Schließen"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
                    <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 px-3.5 py-2.5">
                      <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-0.5">Kampagne</p>
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 break-words">
                        {campaignName}
                      </p>
                      {followupTemplateName ? (
                        <p className="text-[11px] text-neutral-500 mt-1 break-words">
                          Follow-up-Vorlage: {followupTemplateName}
                        </p>
                      ) : null}
                    </div>

                    <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 px-3.5 py-2.5">
                      <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-0.5">Zeitpunkt</p>
                      <p className="text-sm text-neutral-900 dark:text-neutral-100 flex items-start gap-2">
                        <Clock className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                        <span>
                          {ev.allDay
                            ? `${formatDateLong(ev.startAt).replace(/,\s*\d{2}:\d{2}$/, '')} · Ganztägig`
                            : formatDateLong(ev.startAt)}
                        </span>
                      </p>
                    </div>

                    {isBatch ? (
                      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden">
                        <div className="px-3 py-1.5 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between gap-2">
                          <p className="text-[10px] uppercase tracking-wide text-neutral-400">Empfänger</p>
                          <p className="text-[10px] text-neutral-500">
                            {isFollowup
                              ? `${members.length} offen`
                              : `${repliedCount}/${members.length} geantwortet · Klick öffnet Verlauf`}
                          </p>
                        </div>
                        {members.length === 0 ? (
                          <p className="px-3 py-4 text-sm text-neutral-500 text-center">
                            {isFollowup
                              ? 'Keine offenen Empfänger (abgemeldet oder bereits geantwortet).'
                              : 'Keine Empfänger.'}
                          </p>
                        ) : (
                        <ul className="divide-y divide-neutral-100 dark:divide-neutral-800 max-h-48 overflow-y-auto">
                          {members.map((m) => (
                            <li key={m.id}>
                              <button
                                type="button"
                                onClick={() => openCampaignConversation(m, { keepDetail: true })}
                                className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors"
                                title="E-Mail-Verlauf anzeigen"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="text-[13px] leading-snug text-neutral-900 dark:text-neutral-100 truncate flex items-center gap-1.5">
                                    <User className="w-3 h-3 text-neutral-400 flex-shrink-0" />
                                    {m.contact_name || m.contact_email || `Kampagne #${m.id}`}
                                    {Number(m.reply_received) === 1 || Number(m.auto_reply_received) === 1 ? (
                                      <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-400">
                                        · {Number(m.reply_received) === 1 ? 'geantwortet' : 'Auto-Antwort'}
                                      </span>
                                    ) : null}
                                  </p>
                                  <p className="text-[10px] leading-snug text-neutral-500 truncate pl-4">
                                    {[
                                      m.contact_email,
                                      m.contact_company,
                                      campaignStatusLabel(m.status),
                                    ].filter(Boolean).join(' · ')}
                                  </p>
                                </div>
                                <span className="p-1.5 rounded-lg text-neutral-400 flex-shrink-0">
                                  <Mail className="w-3.5 h-3.5" />
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                        )}
                      </div>
                    ) : isFollowup && members.length === 0 ? (
                      <p className="text-sm text-neutral-500 text-center py-3 rounded-xl border border-neutral-200 dark:border-neutral-800">
                        Keine offenen Empfänger (abgemeldet oder bereits geantwortet).
                      </p>
                    ) : (singleMember || campaign)?.contact_name ||
                      (singleMember || campaign)?.contact_email ||
                      (singleMember || campaign)?.contact_company ? (
                      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 px-3.5 py-2.5 space-y-1.5">
                        <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-1">Kontakt</p>
                        {(singleMember || campaign).contact_name ? (
                          <p className="text-sm text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                            {(singleMember || campaign).contact_name}
                          </p>
                        ) : null}
                        {(singleMember || campaign).contact_email ? (
                          <p className="text-sm text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                            <a
                              href={`mailto:${(singleMember || campaign).contact_email}`}
                              className="text-primary-700 dark:text-primary-300 hover:underline truncate"
                            >
                              {(singleMember || campaign).contact_email}
                            </a>
                          </p>
                        ) : null}
                        {(singleMember || campaign).contact_company ? (
                          <p className="text-sm text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
                            <Building2 className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                            {(singleMember || campaign).contact_company}
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    {!isBatch ? (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 px-3.5 py-2.5">
                          <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-0.5">Status</p>
                          <p className="text-sm text-neutral-900 dark:text-neutral-100">
                            {campaignStatusLabel(campaign?.status)}
                          </p>
                        </div>
                        <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 px-3.5 py-2.5">
                          <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-0.5">Follow-ups</p>
                          <p className="text-sm text-neutral-900 dark:text-neutral-100">
                            {campaign
                              ? `${Number(campaign.follow_up_count) || 0}/3 gesendet`
                              : '—'}
                          </p>
                        </div>
                      </div>
                    ) : null}

                    {descLines.length && !isBatch ? (
                      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 px-3.5 py-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-neutral-400 mb-0.5">Details</p>
                        <div className="space-y-0.5">
                          {descLines.map((line) => (
                            <p key={line} className="text-sm text-neutral-700 dark:text-neutral-300 break-words">
                              {line}
                            </p>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {!isBatch && Number(campaign?.reply_received) === 1 ? (
                      <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400 px-0.5">
                        Geantwortet — keine weiteren Kampagnen-Mails
                      </p>
                    ) : !isBatch && Number(campaign?.auto_reply_received) === 1 ? (
                      <p className="text-xs font-medium text-stone-600 dark:text-neutral-300 px-0.5">
                        Auto-Antwort erkannt — Follow-ups laufen weiter
                      </p>
                    ) : null}
                  </div>

                  <div className="px-5 py-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-end gap-2 bg-neutral-50/80 dark:bg-neutral-950/40">
                    <button
                      type="button"
                      className="btn-secondary !py-2"
                      onClick={closeCampaignDetail}
                    >
                      Schließen
                    </button>
                    {!isBatch ? (
                      <button
                        type="button"
                        className="btn-primary !py-2"
                        onClick={() => openCampaignConversation()}
                        disabled={!ev.campaignId && !campaign?.id}
                      >
                        <Mail className="w-4 h-4 mr-1.5" />
                        E-Mail-Verlauf
                      </button>
                    ) : null}
                  </div>
                </>
              );
            })()}
          </div>
        </div>,
        document.body
      )}

      {monthPickerOpen &&
        createPortal(
          <div
            ref={monthPickerPopoverRef}
            className="calendar-month-picker calendar-month-picker--portal"
            style={{ top: pickerPos.top, left: pickerPos.left }}
            role="dialog"
            aria-label="Monat auswählen"
          >
            <div className="calendar-month-picker__head">
              <button
                type="button"
                className="calendar-month-picker__nav"
                onClick={() => setPickerYear((y) => y - 1)}
                aria-label="Vorheriges Jahr"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="calendar-month-picker__year">{pickerYear}</span>
              <button
                type="button"
                className="calendar-month-picker__nav"
                onClick={() => setPickerYear((y) => y + 1)}
                aria-label="Nächstes Jahr"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="calendar-month-picker__grid">
              {MONTH_LABELS.map((label, idx) => {
                const isActive =
                  cursor.getFullYear() === pickerYear && cursor.getMonth() === idx;
                const isCurrent =
                  today.getFullYear() === pickerYear && today.getMonth() === idx;
                return (
                  <button
                    key={label}
                    type="button"
                    className={`calendar-month-picker__month${isActive ? ' is-active' : ''}${
                      isCurrent ? ' is-current' : ''
                    }`}
                    onClick={() => goToMonth(pickerYear, idx)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}

    </div>
  );
};

export default CalendarPage;
